/**
 * Profile Service
 *
 * Handles all profile-related API operations for the current authenticated user.
 * Provides methods for viewing, updating, and managing profile data.
 *
 * @module profileService
 */

import { apiClient } from '@api/client'

/**
 * Profile Service Class
 */
class ProfileService {
	/**
	 * Get current user's profile
	 *
	 * @returns {Promise<Object>} User profile data with role and company details
	 * @throws {Error} If request fails or user not authenticated
	 */
	async getProfile() {
		const response = await apiClient.get('/profile')
		return response.data
	}

	/**
	 * Update profile information
	 *
	 * Only allowed fields: first_name, last_name, phone, settings
	 * Email, role, company, and status cannot be changed via this method
	 *
	 * @param {Object} data - Profile data to update
	 * @param {string} [data.first_name] - First name
	 * @param {string} [data.last_name] - Last name
	 * @param {string} [data.phone] - Phone number
	 * @param {Object} [data.settings] - User settings/preferences
	 * @returns {Promise<Object>} Updated user profile data
	 * @throws {Error} If validation fails or request fails
	 */
	async updateProfile(data) {
		const response = await apiClient.put('/profile', data)
		return response.data
	}

	/**
	 * Upload profile avatar
	 *
	 * Accepts: jpg, jpeg, png, gif, webp
	 * Max size: 2MB
	 *
	 * @param {File} file - Image file to upload
	 * @param {Function} [onProgress] - Progress callback (receives 0-100 percentage)
	 * @returns {Promise<Object>} Response with avatar URL
	 * @throws {Error} If file type/size validation fails or upload fails
	 */
	async uploadAvatar(file, onProgress = null) {
		const response = await apiClient.uploadFile('/profile/avatar', file, {}, onProgress, 'avatar')
		return response.data
	}

	/**
	 * Remove profile avatar
	 *
	 * Deletes the current avatar and clears the avatar field
	 *
	 * @returns {Promise<Object>} Success response
	 * @throws {Error} If no avatar exists or request fails
	 */
	async removeAvatar() {
		const response = await apiClient.delete('/profile/avatar')
		return response
	}

	/**
	 * Change password
	 *
	 * Requires current password for verification.
	 * New password must meet strong policy requirements:
	 * - Minimum 8 characters
	 * - At least one uppercase letter
	 * - At least one lowercase letter
	 * - At least one number
	 * - At least one special character
	 *
	 * @param {string} currentPassword - Current password for verification
	 * @param {string} newPassword - New password
	 * @param {string} confirmPassword - Password confirmation (must match newPassword)
	 * @returns {Promise<Object>} Success response
	 * @throws {Error} If current password is wrong, validation fails, or request fails
	 */
	async changePassword(currentPassword, newPassword, confirmPassword) {
		const response = await apiClient.put('/profile/password', {
			current_password: currentPassword,
			new_password: newPassword,
			confirm_password: confirmPassword,
		})
		return response
	}

	/**
	 * Validate password strength
	 *
	 * Client-side password strength validation matching backend requirements
	 *
	 * @param {string} password - Password to validate
	 * @returns {Object} Validation result with strength indicators
	 */
	validatePasswordStrength(password) {
		const checks = {
			length: password.length >= 8,
			uppercase: /[A-Z]/.test(password),
			lowercase: /[a-z]/.test(password),
			number: /[0-9]/.test(password),
			special: /[^A-Za-z0-9]/.test(password),
		}

		const passedChecks = Object.values(checks).filter(Boolean).length
		const totalChecks = Object.keys(checks).length

		let strength = 'weak'
		if (passedChecks === totalChecks) {
			strength = 'strong'
		} else if (passedChecks >= 3) {
			strength = 'medium'
		}

		return {
			checks,
			passedChecks,
			totalChecks,
			strength,
			isValid: passedChecks === totalChecks,
			percentage: Math.round((passedChecks / totalChecks) * 100),
		}
	}
}

// Export singleton instance
export const profileService = new ProfileService()
export default profileService
